import jsPDF from 'jspdf';
import { formatCurrency, formatNumber } from '../../../utils/formatUtils';
import { COLLECTING_ENTITIES_DETAIL_COLUMNS } from '../constants/collectingEntitiesConstants';

const PAGE_MARGIN_X = 8;
const PAGE_MARGIN_TOP = 12;
const PAGE_MARGIN_BOTTOM = 14;
const ROW_LINE_HEIGHT = 3.8;
const TABLE_HEADER_HEIGHT = 10;

const formatDetailCellValue = (column, value) => {
  if (column.kind === 'amount') {
    return formatCurrency(value, 0, { fallback: '$ 0' });
  }

  if (column.kind === 'quantity') {
    return formatNumber(value, { decimals: 2, fallback: '0,00' });
  }

  if (value === null || value === undefined || value === '') {
    return '-';
  }

  return value.toString();
};

const drawSummaryCards = (doc, yOffset, summary = {}) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const totalWidth = pageWidth - PAGE_MARGIN_X * 2;
  const gap = 6;
  const cardWidth = (totalWidth - gap * 2) / 3;
  const cards = [
    {
      label: 'Registros',
      value: formatNumber(summary.total_records, { decimals: 0, fallback: '0' }),
    },
    {
      label: 'Intereses acumulados',
      value: formatCurrency(summary.total_interest, 0, { fallback: '$ 0' }),
    },
    {
      label: 'Importe acumulado',
      value: formatCurrency(summary.total_amount, 0, { fallback: '$ 0' }),
    },
  ];

  cards.forEach((card, index) => {
    const xOffset = PAGE_MARGIN_X + index * (cardWidth + gap);
    doc.setDrawColor(210, 218, 230);
    doc.setFillColor(249, 250, 252);
    doc.roundedRect(xOffset, yOffset, cardWidth, 18, 2, 2, 'FD');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(card.label, xOffset + 4, yOffset + 7);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(card.value, xOffset + 4, yOffset + 14);
  });

  return yOffset + 24;
};

const drawHeader = (doc, title, header = {}, summary = {}, includeSummary = false) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  let currentY = PAGE_MARGIN_TOP;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(title, pageWidth / 2, currentY, { align: 'center' });
  currentY += 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Actualizado al: ${header.updated_at_date || '-'}`, pageWidth - PAGE_MARGIN_X, currentY, {
    align: 'right',
  });
  currentY += 6;

  doc.setFontSize(8);
  const constructoraLines = doc.splitTextToSize(
    header.constructora_info || '',
    pageWidth - PAGE_MARGIN_X * 2
  );
  constructoraLines.forEach((line) => {
    doc.text(line, PAGE_MARGIN_X, currentY);
    currentY += 4;
  });

  doc.setFontSize(8.5);
  const filterLines = [
    `Plan: ${header.plan_name || '-'} | Entidad: ${header.legal_entity_name || '-'}`,
    `Grupo: ${header.group_name || '-'} | Periodo: ${header.date_filter_text || '-'}`,
  ];
  filterLines.forEach((line) => {
    doc.text(line, PAGE_MARGIN_X, currentY);
    currentY += 5;
  });

  if (includeSummary) {
    currentY = drawSummaryCards(doc, currentY + 2, summary);
  } else {
    currentY += 4;
  }

  return currentY;
};

const drawTableHeader = (doc, yOffset) => {
  let currentX = PAGE_MARGIN_X;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);

  COLLECTING_ENTITIES_DETAIL_COLUMNS.forEach((column) => {
    doc.setDrawColor(180, 188, 201);
    doc.setFillColor(232, 236, 242);
    doc.rect(currentX, yOffset, column.pdfWidth, TABLE_HEADER_HEIGHT, 'FD');

    const labelLines = doc.splitTextToSize(column.label, column.pdfWidth - 2).slice(0, 2);
    const textBaseY = yOffset + (TABLE_HEADER_HEIGHT - labelLines.length * 3) / 2 + 3;

    labelLines.forEach((line, lineIndex) => {
      doc.text(line, currentX + column.pdfWidth / 2, textBaseY + lineIndex * 3, {
        align: 'center',
      });
    });

    currentX += column.pdfWidth;
  });

  return yOffset + TABLE_HEADER_HEIGHT;
};

const drawRow = (doc, yOffset, row) => {
  const columnData = COLLECTING_ENTITIES_DETAIL_COLUMNS.map((column) => {
    const rawValue = formatDetailCellValue(column, row[column.key]);
    const lines = doc.splitTextToSize(rawValue, column.pdfWidth - 2);

    return {
      align: column.align,
      width: column.pdfWidth,
      lines: lines.length ? lines : ['-'],
    };
  });

  const maxLines = Math.max(...columnData.map((cell) => cell.lines.length));
  const rowHeight = maxLines * ROW_LINE_HEIGHT + 2;
  let currentX = PAGE_MARGIN_X;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.8);

  columnData.forEach((cell) => {
    doc.setDrawColor(214, 219, 228);
    doc.rect(currentX, yOffset, cell.width, rowHeight);

    cell.lines.forEach((line, index) => {
      const textY = yOffset + 3.4 + index * ROW_LINE_HEIGHT;
      if (cell.align === 'right') {
        doc.text(line, currentX + cell.width - 1.2, textY, { align: 'right' });
      } else if (cell.align === 'center') {
        doc.text(line, currentX + cell.width / 2, textY, { align: 'center' });
      } else {
        doc.text(line, currentX + 1.2, textY);
      }
    });

    currentX += cell.width;
  });

  return rowHeight;
};

export const useCollectingEntitiesDetailPDF = () => {
  const exportCollectingEntitiesDetailPDF = ({
    title = 'Detalle de entidades recaudadoras',
    filename = 'DetalleEntidadesRecaudadoras',
    header = {},
    summary = {},
    results = [],
  } = {}) => {
    if (!Array.isArray(results) || !results.length) {
      throw new Error('No hay datos disponibles para exportar.');
    }

    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a3',
      });

      let currentY = drawHeader(doc, title, header, summary, true);
      currentY = drawTableHeader(doc, currentY);

      results.forEach((row) => {
        const rowHeight = COLLECTING_ENTITIES_DETAIL_COLUMNS.reduce((maxHeight, column) => {
          const value = formatDetailCellValue(column, row[column.key]);
          const lineCount = doc.splitTextToSize(value, column.pdfWidth - 2).length || 1;
          const calculatedHeight = lineCount * ROW_LINE_HEIGHT + 2;
          return Math.max(maxHeight, calculatedHeight);
        }, 6);

        if (currentY + rowHeight > doc.internal.pageSize.getHeight() - PAGE_MARGIN_BOTTOM) {
          doc.addPage('a3', 'landscape');
          currentY = drawHeader(doc, title, header, summary, false);
          currentY = drawTableHeader(doc, currentY);
        }

        currentY += drawRow(doc, currentY, row);
      });

      doc.save(`${filename}.pdf`);
    } catch (error) {
      console.error('Error exporting collecting entities detail PDF:', error);
      throw new Error('Error al generar el PDF del detalle.');
    }
  };

  return {
    exportCollectingEntitiesDetailPDF,
  };
};
